import React, { useEffect, useMemo } from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { Accordion, Badge, Button, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import RichTextEditor from './RichTextEditor';

const INTRO_PLACEHOLDERS = ['{customer_name}', '{order_number}', '{delivery_date}', '{expected_delivery}', '{delivery_window}', '{delivered_at}', '{tracking_link}', '{order_link}'];
const URL_PATTERN = /^https:\/\/[^\s<>"']+$/;
const COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

// Setting keys contain dots ("button1.label.nl"), which Formik would read as nested paths,
// so the form uses a dot-free name for each field.
const toName = (key) => key.replace(/\./g, '__');
export const toForm = (values) => Object.fromEntries(Object.entries(values).map(([k, v]) => [toName(k), v]));
const fromForm = (values, fieldKeys) => Object.fromEntries(fieldKeys.map((k) => [k, values[toName(k)]]));

const allFields = (schema) => schema.flatMap((g) => g.rows.flatMap((r) => r.fields));

// One Yup schema, built from the schema the backend sends — the backend stays the authority
// (it re-validates everything on save) but problems show while typing.
function buildValidation(schema, t) {
    const shape = {};
    for (const f of allFields(schema)) {
        let rule = Yup.string();
        if (f.type === 'url') {
            rule = rule.test('https', t('emailTemplates.layout.errors.url'), (v) => !v || URL_PATTERN.test(v));
        } else if (f.type === 'color') {
            rule = rule.matches(COLOR_PATTERN, t('emailTemplates.layout.errors.color'));
        } else if (f.type === 'select') {
            rule = rule.oneOf(f.options, t('emailTemplates.layout.errors.select'));
        } else if (f.type === 'bool') {
            rule = rule.oneOf(['true', 'false'], t('emailTemplates.layout.errors.bool'));
        }
        if (f.type !== 'html') rule = rule.trim();
        if (f.max) rule = rule.max(f.max, t('emailTemplates.layout.errors.too_long'));
        if (f.required) rule = rule.required(t('emailTemplates.layout.errors.required'));
        shape[toName(f.key)] = rule;
    }
    return Yup.object().shape(shape);
}

// Pushes the live form values (dotted keys) and validity up to the page, which feeds the
// preview and decides whether Save is allowed.
function SyncToParent({ fieldKeys, onChange, onValidity }) {
    const { values, errors } = useFormikContext();
    useEffect(() => { onChange(fromForm(values, fieldKeys)); }, [values]); // eslint-disable-line react-hooks/exhaustive-deps
    const invalid = Object.keys(errors).length > 0;
    useEffect(() => { onValidity(!invalid); }, [invalid]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
}

function FieldInput({ field, lang }) {
    const { t } = useTranslation();
    const formik = useFormikContext();
    const name = toName(field.key);
    const value = formik.values[name] ?? '';
    const set = (v) => formik.setFieldValue(name, v);
    const invalid = !!formik.errors[name];

    if (field.type === 'bool') {
        return (
            <Form.Check
                type="switch"
                id={`layout-${name}`}
                checked={value === 'true'}
                onChange={(e) => set(e.target.checked ? 'true' : 'false')}
            />
        );
    }
    if (field.type === 'select') {
        return (
            <Form.Select value={value} onChange={(e) => set(e.target.value)} isInvalid={invalid}>
                {field.options.map((o) => <option key={o} value={o}>{t(`emailTemplates.layout.options.${o}`)}</option>)}
            </Form.Select>
        );
    }
    if (field.type === 'color') {
        return (
            <div className="d-flex align-items-center gap-2">
                <Form.Control
                    type="color"
                    className="form-control-color email-layout-color"
                    value={COLOR_PATTERN.test(value) ? value : '#000000'}
                    onChange={(e) => set(e.target.value.toUpperCase())}
                />
                <Form.Control value={value} onChange={(e) => set(e.target.value)} isInvalid={invalid} maxLength={7} />
            </div>
        );
    }
    if (field.type === 'html') {
        return <RichTextEditor value={value} onChange={set} placeholderTokens={INTRO_PLACEHOLDERS} />;
    }
    return (
        <Form.Control
            value={value}
            onChange={(e) => set(e.target.value)}
            isInvalid={invalid}
            placeholder={field.type === 'url' ? 'https://' : (field.default || '')}
            maxLength={field.max}
            lang={lang}
        />
    );
}

function FieldRow({ row, lang, serverErrors }) {
    const { t } = useTranslation();
    const formik = useFormikContext();
    const paired = row.fields.some((f) => f.lang);
    const problems = row.fields
        .map((f) => formik.errors[toName(f.key)]
            || (serverErrors[f.key] && t(`emailTemplates.layout.errors.${serverErrors[f.key].code}`)))
        .filter(Boolean);
    return (
        <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">{row.label[lang]}</Form.Label>
            <Row className="g-2">
                {row.fields.map((f) => (
                    <Col key={f.key} md={paired || row.fields.length > 1 ? true : 12}>
                        {f.lang && (
                            <small className="text-muted d-block mb-1">
                                {t(f.lang === 'nl' ? 'emailTemplates.layout.dutch' : 'emailTemplates.layout.english')}
                            </small>
                        )}
                        <FieldInput field={f} lang={f.lang} />
                    </Col>
                ))}
            </Row>
            {row.hint && row.hint[lang] && <Form.Text className="text-muted">{row.hint[lang]}</Form.Text>}
            {problems.length > 0 && <div className="text-danger small mt-1">{problems[0]}</div>}
        </Form.Group>
    );
}

function Sections({ schema, lang, serverErrors, onReset }) {
    const { t } = useTranslation();
    const formik = useFormikContext();

    const confirmReset = async (section) => {
        const result = await Swal.fire({
            title: t('emailTemplates.layout.resetConfirmTitle'),
            text: t('emailTemplates.layout.resetConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('emailTemplates.layout.resetConfirmButton'),
            cancelButtonText: t('common.cancel'),
        });
        if (result.isConfirmed) onReset(section);
    };

    return (
        <Accordion alwaysOpen defaultActiveKey={['intro']} className="email-layout-sections">
            {schema.map((group) => {
                const keys = group.rows.flatMap((r) => r.fields.map((f) => f.key));
                const count = keys.filter((k) => formik.errors[toName(k)] || serverErrors[k]).length;
                return (
                    <Accordion.Item eventKey={group.id} key={group.id}>
                        <Accordion.Header>
                            {group.title[lang]}
                            {count > 0 && <Badge bg="danger" className="ms-2">{t('emailTemplates.layout.problems', { count })}</Badge>}
                        </Accordion.Header>
                        <Accordion.Body>
                            {group.rows.map((row) => (
                                <FieldRow key={row.fields[0].key} row={row} lang={lang} serverErrors={serverErrors} />
                            ))}
                            <div className="text-end">
                                <Button variant="outline-secondary" size="sm" onClick={() => confirmReset(group.id)}>
                                    {t('emailTemplates.layout.reset')}
                                </Button>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                );
            })}
        </Accordion>
    );
}

// The editable settings of the branded order email, grouped in sections and generated from the
// backend schema (so a new setting there appears here without frontend changes).
export default function EmailLayoutSettings({ schema, initialValues, serverErrors, onChange, onValidity, onReset }) {
    const { i18n, t } = useTranslation();
    const lang = (i18n.language || 'en').startsWith('nl') ? 'nl' : 'en';
    const validationSchema = useMemo(() => buildValidation(schema, t), [schema, t]);
    const fieldKeys = useMemo(() => allFields(schema).map((f) => f.key), [schema]);

    return (
        <Formik
            initialValues={toForm(initialValues)}
            validationSchema={validationSchema}
            validateOnMount
            onSubmit={() => {}}
        >
            <>
                <SyncToParent fieldKeys={fieldKeys} onChange={onChange} onValidity={onValidity} />
                <Sections schema={schema} lang={lang} serverErrors={serverErrors} onReset={onReset} />
            </>
        </Formik>
    );
}
